import './ModuleDetailsHeader.scss';
import '../../HeaderCommon.scss';
import { IconButton, Skeleton } from '@mui/material';
import { ReactComponent as ChevronLeft } from 'images/icons/ChevronLeftThin.svg';
import { ReactComponent as Report } from 'images/icons/ReportFilled.svg';
import { ReactComponent as Crown } from 'images/icons/Crown.svg';
import { ReactComponent as Medal } from 'images/icons/MedalBold.svg';
import SectionTitle from 'components/TextComps/SectionTitle';
import { useNavigate } from 'react-router-dom';
import { Trans } from 'react-i18next';

type ModuleDetailsHeaderProps = {
  title: string | undefined;
  shortDescription: string | undefined;
  navigationPath: string;
  imageUrl?: string;
  numberOfLessons?: number;
  numberOfChapterTests?: number;
  numberOfCertificates?: number;
  isPending: boolean;
};

export const ModuleDetailsHeader = ({
  title,
  shortDescription,
  navigationPath,
  imageUrl,
  numberOfLessons,
  numberOfChapterTests,
  numberOfCertificates,
  isPending,
}: ModuleDetailsHeaderProps) => {
  const navigate = useNavigate();

  return (
    <header className='module-details-header-wrapper'>
      {!isPending ? (
        <>
          <div className='learning-hub-module-grid-container header-content'>
            <IconButton className='learning-hub-back-button' onClick={() => navigate(navigationPath)}>
              <ChevronLeft className='back-icon' />
            </IconButton>
            <div className='learning-hub-title'>
              <SectionTitle>
                <>{title}</>
              </SectionTitle>
            </div>

            <span className='learning-hub-short-description'>{shortDescription}</span>
            {numberOfCertificates || numberOfChapterTests || numberOfLessons ? (
              <div className='details-summary'>
                {numberOfLessons && numberOfLessons > 0 ? (
                  <div className='details-summary-entry'>
                    <Report className='lessons-icon' />
                    <Trans
                      i18nKey={numberOfLessons > 1 ? 'NumberOfLessons' : 'SingleLesson'}
                      ns='learningHub'
                      defaults={`<bold>{{numberOfLessons}}</bold> ${numberOfLessons > 1 ? 'Lessons' : 'Lesson'} (Free)`}
                      values={{ lessons: numberOfLessons }}
                      components={{ bold: <strong /> }}
                    />
                  </div>
                ) : (
                  <></>
                )}
                {numberOfChapterTests && numberOfChapterTests > 0 ? (
                  <div className='details-summary-entry'>
                    <Crown className='chapter-tests-icon' />
                    <Trans
                      i18nKey={numberOfChapterTests > 1 ? 'NumberOfChapterTests' : 'SingleChapterTest'}
                      ns='learningHub'
                      defaults={`<bold>{{numberOfChapterTests}}</bold> Chapter ${numberOfChapterTests > 1 ? 'Tests' : 'Test'}`}
                      values={{ tests: numberOfChapterTests }}
                      components={{ bold: <strong /> }}
                    />
                  </div>
                ) : (
                  <></>
                )}
                {numberOfCertificates && numberOfCertificates > 0 ? (
                  <div className='details-summary-entry'>
                    <Medal className='certificates-icon' />
                    <Trans
                      i18nKey={numberOfCertificates > 1 ? 'NumberOfCertificationExams' : 'SingleCertificationExam'}
                      ns='learningHub'
                      defaults={`<bold>{{numberOfCertificates}}</bold> Certification ${numberOfCertificates > 1 ? 'Exams' : 'Exam'}`}
                      values={{ certifications: numberOfCertificates }}
                      components={{ bold: <strong /> }}
                    />
                  </div>
                ) : (
                  <></>
                )}
              </div>
            ) : (
              <></>
            )}
          </div>
          {imageUrl && (
            <div
              className='image-wrapper'
              style={{ backgroundImage: `linear-gradient(to right, white, transparent), url(${imageUrl})` }}
            ></div>
          )}
        </>
      ) : (
        <div className='header-loader-wrapper'>
          <Skeleton variant='text' width={200} sx={{ marginLeft: '1.5rem' }} />
          <Skeleton variant='text' width={450} sx={{ marginLeft: '1.5rem' }} />
          <div>
            <Skeleton variant='text' sx={{ marginLeft: '1.5rem' }} width={100} />
            <Skeleton variant='text' width={100} />
            <Skeleton variant='text' width={100} />
          </div>
        </div>
      )}
    </header>
  );
};
